<template>
  <div class="order-steps-container">
    <arch
      @go-to-step="goToStep"
      :titles="steps"
      :activeStep="activeStep"
      :errors="errors"
      @submit-order="sendOrder"
    ></arch>
    <router-view></router-view>
  </div>
</template>

<script>
import Arch from '@/components/Arch.vue'

import { mapStores } from 'pinia'

import isEmpty from 'lodash-es/isEmpty'
import { EventBus } from '@/eventbus/EventBus'
import SendingOrder from '@/dialogs/SendingOrder.vue'
import ErrorMessage from '@/dialogs/ErrorMessage.vue'
import OrderConfirm from '@/dialogs/OrderConfirm.vue'

import OrderMixin from '@/mixins/OrderMixin'
import { saveOrder } from '@/api/api'
import { useFileListStore } from '@/stores/FileList.store'
import { useShopsStore } from '@/stores/Shops.store'
import { usePrintProductSecuredSAStore } from '@/features/printProductSecuredSA/PrintProductSecuredSA.store'
import Cookies from 'js-cookie'
import { UPLOAD_COOKIE } from '@/stores/store'

export default {
  data() {
    return {
      step: 'step1',
      steps: ['Printen', 'Bezorgen', 'Factureren', 'Controleren'],
      dates: [],
      secure: true,
      inProgress: false
    }
  },
  mixins: [OrderMixin],
  components: {
    Arch
  },
  methods: {
    goToStep(step) {
      switch (step) {
        case 'step1':
          this.$router.push({
            name: 'printProductSecuredSA'
          })
          break
        case 'step2':
          this.$router.push({
            name: 'printProductSecuredSADeliver'
          })
          break
        case 'step3':
          this.$router.push({
            name: 'printProductSecuredSAInvoice'
          })
          break
        case 'step4':
          this.$router.push({
            name: 'printProductSecuredSAConfirm'
          })
          break
      }
    },
    updateOrder(key, val) {
      this.printProductSecuredSAStore.updateOrderDetails(key, val)
    },
    sendOrder() {
      if (this.inProgress) return
      this.inProgress = true
      //const order = cloneDeep(this.orderDetails)

      const order = this.prepareOrder(
        this.printProductSecuredSAStore.orderDetails
      )

      if (!order) {
        this.inProgress = false
        return
      }

      //add files to order
      order.cart = [...this.fileListStore.fileList]
      order.cart.forEach((file) => {
        file.printType = 'PRINT_TEMPLATE'
      })

      //set name if pickup
      if (order.orderType === 'pickUp') {
        order.deliveryAddress.name = this.rootStore.user.name
      }

      //set order stuff
      order.themeID = this.rootStore.theme.themeID
      order.mainThemeName = this.rootStore.theme.mainThemeName

      order.sessionId = Cookies.get(UPLOAD_COOKIE)

      order.product = 'printProduct'

      this.$modal.show(SendingOrder, null, {
        height: 120,
        width: 280,
        adaptive: true,
        name: 'sending'
      })

      saveOrder(order).then(
        (resp) => {
          this.$modal.hide('sending')
          this.inProgress = false

          this.fileListStore.removeAllFiles()

          this.printProductSecuredSAStore.resetOrderDetails()

          this.$modal.show(
            OrderConfirm,
            {},
            {
              height: 'auto',
              width: 360,
              adaptive: true,
              clickToClose: false
            },
            {
              'before-close': () => {
                this.step = 'step1'
              }
            }
          )
        },
        (resp) => {
          this.$modal.hide('sending')
          this.inProgress = false
          this.$modal.show(
            ErrorMessage,
            {
              title: 'Probleem bij het plaatsen van uw order',
              text: resp.message
            },
            {
              height: 250,
              width: 380,
              adaptive: true
            }
          )
        }
      )
    }
  },
  computed: {
    ...mapStores(
      usePrintProductSecuredSAStore,
      useShopsStore,
      useFileListStore
    ),
    orderDetails() {
      return this.printProductSecuredSAStore.orderDetails
    },
    shop() {
      return this.printProductSecuredSAStore.shop
    },
    errors() {
      return this.printProductSecuredSAStore.orderErrors.length
    },
    shops() {
      return this.shopsStore.shopsList
    },
    activeStep() {
      if (this.$route.name === 'printProductSecuredSA') {
        return 'step1'
      } else if (this.$route.name === 'printProductSecuredSADeliver') {
        return 'step2'
      } else if (this.$route.name === 'printProductSecuredSAInvoice') {
        return 'step3'
      } else if (this.$route.name === 'printProductSecuredSAConfirm') {
        return 'step4'
      }
    },
    customerNumber() {
      return this.printProductSecuredSAStore.orderDetails.invoiceAddress
        .newCustomer
        ? 0
        : this.printProductSecuredSAStore.orderDetails.invoiceAddress
            .customerNumber || 0
    }
  },
  created() {
    this.fileListStore.fileList.forEach((file) => {
      if (file.productSpecs.specs && file.productSpecs.specs.length > 0) {
        this.fileListStore.addProductOrderProduct([
          [file.fileName],
          { title: '', specs: [] }
        ])
      }
    })

    if (isEmpty(this.printProductSecuredSAStore.orderDetails.deliveryAddress)) {
      const deliveryAddress = this.setDeliveryAddress()

      this.printProductSecuredSAStore.updateOrderDetails(
        'deliveryAddress',
        deliveryAddress
      )
    }

    if (isEmpty(this.printProductSecuredSAStore.orderDetails.invoiceAddress)) {
      const invoiceDetails = this.getDefaultInvoiceDetails()

      this.printProductSecuredSAStore.updateOrderDetails(
        'invoiceAddress',
        invoiceDetails.address
      )

      this.printProductSecuredSAStore.updateProject(invoiceDetails.project)

      this.printProductSecuredSAStore.updateOrderDetails(
        'reference',
        invoiceDetails.reference
      )
    }
    this.printProductSecuredSAStore.updateOrderDetails('secure', true)

    this.printProductSecuredSAStore.getTheShop({
      deliveryAddress: this.printProductSecuredSAStore.orderDetails
        .deliveryAddress,
      invoiceId: this.customerNumber,
      secure: true
    })

    EventBus.on('submitOrder', this.sendOrder)
  },
  unmounted() {
    EventBus.off('submitOrder', this.sendOrder)
  },
  watch: {
    'printProductSecuredSAStore.orderDetails.deliveryAddress': function () {
      this.printProductSecuredSAStore.getTheShop({
        deliveryAddress: this.printProductSecuredSAStore.orderDetails
          .deliveryAddress,
        invoiceId: this.customerNumber,
        secure: this.printProductSecuredSAStore.orderDetails.secure
      })
    },
    'printProductSecuredSAStore.orderDetails.invoiceAddress': function () {
      this.printProductSecuredSAStore.getTheShop({
        deliveryAddress: this.printProductSecuredSAStore.orderDetails
          .deliveryAddress,
        invoiceId: this.customerNumber,
        secure: this.printProductSecuredSAStore.orderDetails.secure
      })
    },
    shop: function (val) {
      this.printProductSecuredSAStore
        .getTheDates({
          shopId: this.shop.id,
          invoiceId: this.customerNumber,
          secure: this.printProductSecuredSAStore.orderDetails.secure
        })
        .then((dates) => {
          if (
            isEmpty(this.printProductSecuredSAStore.orderDetails.deliveryDate)
          ) {
            let delDate = {}
            delDate = dates.find((date) => {
              return date.active
            })

            delDate.time = delDate.times[0].time

            this.printProductSecuredSAStore.updateOrderDetails(
              'deliveryDate',
              delDate
            )
          }
        })
    }
  }
}
</script>
<style lang="scss">
/* #print-store {
  max-width: 840px;
} */
</style>
